import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  message,
  notification,
  Pagination,
  Progress,
  Segmented,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { api } from "api";
import { ApiService } from "api/api.service";
import { T_Api_Success_Res } from "api/api.types";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import PageTitle from "components/PageTitle";
import { loader } from "components/QueryWrapper";
import { BATCH_TYPE, CASE_STATUS, CaseSchemaDto, PROCESS_STATUS, UPLOAD_TYPE } from "dtos/leadCases.dto";
import DashboardLayout from "layout/DashboardLayout";
import { CSSProperties, useEffect, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { LuPackageSearch } from "react-icons/lu";
import {
  TbAdjustments,
  TbBuilding,
  TbCloudUpload,
  TbDownload,
  TbExternalLink,
  TbLoader2,
  TbPlayerPlayFilled,
  TbProgressAlert,
  TbRefresh,
  TbSearch,
  TbTableExport,
  TbUsers,
  TbUserSearch,
  TbWorldSearch,
} from "react-icons/tb";
import { addToQueue, splitQueueByStatus, useApiQueue } from "utils/apiStoreQueue";
import { formatNumberShort, getErrorMessage, numberParser, phoneFormatter, toTitleCase } from "utils/functions";
import { queryClient } from "utils/queryClient";
import styles from "./LeadCases.module.scss";
import { T_Filters, T_ShowColumns } from "./LeadCases.utils";
import { AdvancedFiltersDrawer } from "./components/AdvancedFiltersDrawer";
import { AssignCases } from "./components/AssignCases";
import { DetailsDrawer } from "./components/DetailsDrawer";
import { FaFacebook, FaLinkedin } from "react-icons/fa";
import { CASE_STATUS_COLOURS, CASE_STATUS_ICONS } from "utils/constants";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import Drag from "pages/Drag/Drag";
import DynamicUpload from "components/DynamicUpload/DynamicUpload";
import dayjs from "dayjs";

const maxSelect = {
  split: 50,
  pc: 20,
  bc: 99,
};

const columns: ColumnsType<CaseSchemaDto> = [
  {
    key: "defendant",
    dataIndex: "defendant",
    title: "Defendant",
    width: 200,
    fixed: "left",
  },
  {
    key: "person_name",
    title: "Owner Name",
    render: (_, record) => {
      const personName = `${record.first_name ?? ""} ${record.last_name ?? ""}`;
      const searchStr = `${[record?.first_name, record?.last_name].join(" ")}`;
      return (
        <div className="flex justify-between items-center gap-2">
          <Typography.Text>{personName}</Typography.Text>
          {personName && personName?.length > 1 ? (
            <Button
              size="small"
              variant="filled"
              color="default"
              target="_blank"
              rel="noreferrer noopener"
              href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(searchStr)}`}
              icon={<TbUserSearch />}
            />
          ) : null}
        </div>
      );
    },
    width: 160,
    fixed: "left",
  },
  {
    key: "business_name",
    dataIndex: "business_name",
    title: "Business Name",
    width: 240,
    render: (_, record) => {
      const businessName = record?.business_name;
      return (
        <div className="flex justify-between items-center gap-2">
          <Typography.Text>{businessName}</Typography.Text>
          {businessName && businessName?.length > 1 ? (
            <Button
              size="small"
              variant="filled"
              color="default"
              target="_blank"
              rel="noreferrer noopener"
              href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(businessName)}`}
              icon={<TbBuilding />}
            />
          ) : null}
        </div>
      );
    },
  },
  {
    key: "assigned_to",
    dataIndex: "assigned_to",
    title: "Assigned to",
    width: 180,
    render: (_, record) => {
      return <>{(record?.assigned_to as any)?.name}</>;
    },
  },
  {
    key: "email",
    dataIndex: "email",
    title: "Email",
    width: 200,
    render: (_, record) => {
      return (
        <a target="_blank" rel="noreferrer noopener" href={`mailto:${record.email}`}>
          {record.email}
        </a>
      );
    },
  },
  {
    key: "phone",
    dataIndex: "phone",
    title: "Phone",
    width: 200,
    render: (_, record) => {
      return (
        <a target="_blank" rel="noreferrer noopener" href={`tel:${record.phone}`}>
          {record.phone}
        </a>
      );
    },
  },
  {
    key: "linkedin",
    dataIndex: "linkedin",
    title: "Linkedin",
    width: 200,
    render: (_, record) => {
      if (record.linkedin && record.linkedin?.length > 0) {
        return (
          <a target="_blank" rel="noreferrer noopener" href={`${record.linkedin}`}>
            {record.linkedin}
          </a>
        );
      } else if (record?.business_name || record?.first_name || record?.last_name) {
        const searchStr = `${record?.business_name ? `${record?.business_name}, ` : ""}${[record?.first_name, record?.last_name].join(" ")} site:linkedin.com`;
        return (
          <Button
            size="small"
            variant="filled"
            color="default"
            target="_blank"
            rel="noreferrer noopener"
            href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(searchStr)}`}
            icon={<TbSearch />}
          >
            Search for LinkedIn
          </Button>
        );
      }
    },
  },
  {
    key: "facebook",
    dataIndex: "facebook",
    title: "Facebook",
    width: 200,
    render: (_, record) => {
      if (record.facebook && record.facebook?.length > 0) {
        return (
          <a target="_blank" rel="noreferrer noopener" href={`${record.facebook}`}>
            {record.facebook}
          </a>
        );
      } else if (record?.business_name || record?.first_name || record?.last_name) {
        const searchStr = `${record?.business_name ? `${record?.business_name}` : ""} site:facebook.com`;
        return (
          <Button
            size="small"
            variant="filled"
            color="default"
            target="_blank"
            rel="noreferrer noopener"
            href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(searchStr)}`}
            icon={<TbSearch />}
          >
            Search for Facebook
          </Button>
        );
      }
    },
  },
  {
    key: "amount",
    dataIndex: "amount",
    title: "Amount",
    width: 100,
    align: "right",
    render: (_, record) => {
      return <span style={{ fontVariantNumeric: "tabular-nums" }}>{record?.amount ?? "-"}</span>;
    },
  },
  {
    key: "status",
    dataIndex: "status",
    title: "Status",
    width: 270,
    render: (_, record) => {
      return (
        <Tag icon={CASE_STATUS_ICONS[record.status]} color={CASE_STATUS_COLOURS[record.status]}>
          {record.status?.toUpperCase()}
        </Tag>
      );
    },
  },
  {
    key: "caseNumber",
    dataIndex: "caseNumber",
    title: "Case Number",
    width: 120,
  },
  {
    key: "state",
    dataIndex: "state",
    title: "State",
  },
  {
    key: "city",
    dataIndex: "city",
    title: "City",
    width: 100,
  },
  {
    key: "court",
    dataIndex: "court",
    title: "Court",
    width: 150,
  },
  {
    key: "judge",
    dataIndex: "judge",
    title: "Judge",
    width: 100,
  },
  {
    key: "plaintiff",
    dataIndex: "plaintiff",
    title: "Plain tiff",
    width: 150,
  },
  {
    key: "plaintiffLawyers",
    dataIndex: "plaintiffLawyers",
    title: "Plain tiff Lawyers",
    width: 200,
  },
  {
    key: "plaintiffLawFirm",
    dataIndex: "plaintiffLawFirm",
    title: "Plain tiff Law Firm",
    width: 200,
  },
  {
    key: "defendantLawyers",
    dataIndex: "defendantLawyers",
    title: "Defendant Lawyers",
    width: 200,
  },
  {
    key: "defendantLawFirm",
    dataIndex: "defendantLawFirm",
    title: "Defendant Law Firm",
    width: 200,
  },
  {
    key: "natureOfSuit",
    dataIndex: "natureOfSuit",
    title: "Nature Of Suit",
    width: 160,
  },
  {
    key: "natureOfSuitId",
    dataIndex: "natureOfSuitId",
    title: "Nature Of Suit Id",
    width: 160,
  },
  {
    key: "step",
    dataIndex: "step",
    title: "Step",
    width: 100,
    render: (_, record) => {
      return <Tag>{record.step?.toUpperCase()}</Tag>;
    },
  },
  {
    key: "trade_name",
    dataIndex: "trade_name",
    title: "Trade name",
    width: 100,
  },
  {
    key: "other_business_names",
    dataIndex: "other_business_names",
    title: "Other business names",
    render: (_, record) => record["other_business_names"]?.join(", "),
    width: 200,
  },
  {
    key: "nicknames",
    dataIndex: "nicknames",
    title: "Nicknames",
    render: (_, record) => record["nicknames"]?.join(", "),
    width: 100,
  },
  {
    key: "other_business_owners",
    dataIndex: "other_business_owners",
    title: "Other business owners",
    render: (_, record) => record["other_business_owners"]?.join(", "),
    width: 200,
  },
  {
    key: "type_of_business",
    dataIndex: "type_of_business",
    title: "Type of business",
    width: 160,
  },
  {
    key: "case_type",
    dataIndex: "case_type",
    title: "Case type",
    width: 100,
  },
  {
    key: "issue",
    dataIndex: "issue",
    title: "Issue",
    width: 300,
  },
  {
    key: "actions",
    title: "",
    fixed: "right",
  },
];

const allStatuses = Object.values(CASE_STATUS);
const processed = [CASE_STATUS.BOTH_FOUND, CASE_STATUS.EMAIL_FOUND, CASE_STATUS.LI_FOUND, CASE_STATUS.PHONE_FOUND];
const notProcessed = allStatuses.filter((el) => processed.includes(el) === false);

const searchEngineUrls = {
  google: "https://www.google.com/search?q=",
  duckduckgo: "https://duckduckgo.com/?q=",
};

export default function LeadCases() {
  const [filters, setFilters] = useState<T_Filters>({
    page: 1,
    limit: 20,
    batchId: "",
    filter: {},
    statuses: allStatuses,
    assign_match: true,
    batchType: BATCH_TYPE.CASE_PORTAL,
  });
  const [openDynamicModalOpen, setOpenDynamicModalOpen] = useState(false);

  const detAccess = ApiService.lead.useOpenDET();

  // useEffect(() => {
  //   if (detAccess.data?.result.url) {
  //     window.open(detAccess.data.result.url, "_blank");
  //   }
  // }, [detAccess.data?.result.url]);

  const allBatches = ApiService.lead.useGetAllBatches({ type: filters.batchType });
  const batchCases = ApiService.lead.useGetLeadCases(filters);
  const batchStatus = ApiService.lead.useGetBatchStatus(filters.batchId, {
    assigned_to: filters.assigned_to,
    assign_match: filters.assign_match,
  });
  const runCases = ApiService.lead.useRunCases();
  const downloadCsv = ApiService.lead.useDownloadCases();
  const exportEmailFoundCsv = ApiService.lead.useExportEmailFoundCasesCsv();
  const assignCases = ApiService.lead.useAssignCases();
  const paginatedAllUsers = ApiService.user.useGetPaginated({ page: 1, limit: 100, role: [] });
  const caseDiscardedStep = ApiService.lead.useDiscardedStep();

  const [showColumns, setShowColumns] = useState<T_ShowColumns>({
    defendant: true,
    person_name: true,
    business_name: true,
    linkedin: true,
    facebook: true,
    email: true,
    phone: true,
    amount: true,
    status: true,
    caseNumber: false,
    state: false,
    city: false,
    court: false,
    judge: false,
    plaintiff: false,
    plaintiffLawyers: false,
    plaintiffLawFirm: false,
    defendantLawyers: false,
    defendantLawFirm: false,
    natureOfSuit: false,
    natureOfSuitId: false,
    step: true,
    trade_name: false,
    other_business_names: false,
    nicknames: false,
    other_business_owners: false,
    type_of_business: false,
    case_type: true,
    issue: false,
    actions: true,
    assigned_to: true,
  });
  const [caseId, setCaseId] = useState<string>();
  const [progress, setProgress] = useState(0);
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([]);
  const [isAdvFiltersDrawerOpen, setIsAdvFiltersDrawerOpen] = useState(false);
  const [isEditModeOn, setIsEditModeOn] = useState(false);
  const [status, setStatus] = useState<CASE_STATUS[]>([CASE_STATUS.EMAIL_FOUND, CASE_STATUS.BOTH_FOUND]);

  useEffect(() => {
    const localStored = localStorage.getItem("columns");
    if (localStored) {
      const storedColumns = JSON.parse(localStored);
      setShowColumns(storedColumns);
    }
  }, []);

  function handleRun(method: "split" | "pc" | "bc" | "pcpl" | "discard", caseIds: string[]) {
    if (method in maxSelect && selectedCaseIds.length > (maxSelect as any)[method]) {
      return message.error(
        `You can only select upto ${(maxSelect as any)[method]} cases at a time for this action. Deselect a few and try again`,
      );
    }
    if (method === "pc" || method === "split") {
      notification.warning({
        message:
          "Please stay on this page while the request is being processed. If you go away the process might get stopped",
        key: "run_cases",
        duration: 6,
      });
    }
    runCases.mutate(
      {
        method,
        case_ids: caseIds,
        rerun: true,
      },
      {
        onSuccess: (data, variables) => {
          if (variables.method === "pc") {
            notification.success({
              message: `LinkedIn url ${data.result?.linkedin_url_found} found.${data.result?.linkedin_url_not_found} not found.`,
              key: "run_cases",
            });
            return queryClient.invalidateQueries({ queryKey: ["lead", "case", filters] });
          } else if (variables.method === "bc") {
            notification.info({
              message:
                "Requested case(s) are processing. You can do other things in the meanwhile. Please refresh and check after a few minutes",
            });
          } else if (variables.method === "split") {
            notification.success({
              message: "Request processing completed",
              key: "run_cases",
            });
            return queryClient.invalidateQueries({ queryKey: ["lead", "case", filters] });
          }
        },
      },
    );
  }

  const queue = useApiQueue();
  const { notErroredQ, erroredQ } = splitQueueByStatus(queue);
  const onError = (item: any) => {
    // const failedElement = document.getElementById(item._id);
    // if (failedElement) failedElement.style.border = "2px solid red";
  };
  const editColumns: ColumnsType<CaseSchemaDto> = [
    {
      key: "defendant",
      dataIndex: "defendant",
      title: "Defendant",
      width: 260,
      fixed: "left",
    },
    {
      key: "person_name",
      title: "Owner Name",
      render: (_, record) => {
        const personName = `${record.first_name ?? ""} ${record.last_name ?? ""}`;
        const searchStr = `${[record?.first_name, record?.last_name].join(" ")}`;
        return (
          <div className="flex justify-between items-center gap-2">
            <Typography.Text>{personName}</Typography.Text>
            {personName && personName?.length > 1 ? (
              <Button
                size="small"
                variant="filled"
                color="default"
                target="_blank"
                rel="noreferrer noopener"
                href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(searchStr)}`}
                icon={<TbUserSearch />}
              />
            ) : null}
          </div>
        );
      },
      width: 200,
      fixed: "left",
    },
    {
      key: "business_name",
      dataIndex: "business_name",
      title: "Business Name",
      width: 240,
      render: (_, record) => {
        const businessName = record?.business_name;
        return (
          <div className="flex justify-between items-center gap-2">
            <Typography.Text>{businessName}</Typography.Text>
            {businessName && businessName?.length > 1 ? (
              <Button
                size="small"
                variant="filled"
                color="default"
                target="_blank"
                rel="noreferrer noopener"
                href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(businessName)}`}
                icon={<TbBuilding />}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      key: "email",
      dataIndex: "email",
      title: "Email",
      width: 240,
    },
    {
      key: "phone",
      dataIndex: "phone",
      title: "Phone",
      width: 200,
    },
    {
      key: "actions",
      title: "",
      fixed: "right",
      width: 380,
      render: (_, record) => {
        const searchStr = `${record?.business_name ? `${record?.business_name}, ` : ""}${[record?.first_name, record?.last_name].join(" ")}`;
        const fbSearchStr = `${record?.business_name ? `${record?.business_name}` : ""} site:facebook.com`;
        const linkedInSearchStr = `${record?.business_name ? `${record?.business_name}, ` : ""}${[record?.first_name, record?.last_name].join(" ")} site:linkedin.com`;

        return (
          <div className="flex items-center gap-3">
            <Dropdown
              menu={{
                items: [
                  PROCESS_STATUS.NOT_REACHABLE,
                  PROCESS_STATUS.NOT_FOUND,
                  PROCESS_STATUS.WRONG_CONTACT,
                  PROCESS_STATUS.OTHERS,
                ].map((el) => ({ key: el, label: toTitleCase(el) })),
                onClick: ({ key }) => {
                  caseDiscardedStep.mutate(
                    { step: key as PROCESS_STATUS, _id: record._id },
                    {
                      onSuccess: () => {
                        message.success("Step updated");
                      },
                    },
                  );
                },
              }}
            >
              <Button
                size="small"
                icon={<TbProgressAlert />}
                loading={caseDiscardedStep.variables?._id === record?._id && caseDiscardedStep.isPending}
              >
                Change Step
              </Button>
            </Dropdown>
            <Tooltip trigger={"hover"} title={"LinkedIn Search"}>
              <Button
                size="small"
                target="_blank"
                rel="noreferrer noopener"
                href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(linkedInSearchStr)}`}
                icon={<FaLinkedin />}
                style={{ color: "#0077B5" }}
              />
            </Tooltip>
            <Tooltip trigger={"hover"} title={"Facebook Search"}>
              <Button
                size="small"
                target="_blank"
                rel="noreferrer noopener"
                href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(fbSearchStr)}`}
                icon={<FaFacebook />}
                style={{ color: "#316FF6" }}
              />
            </Tooltip>
            <Button
              className={styles.table_actions}
              size="small"
              target="_blank"
              rel="noreferrer noopener"
              href={`${searchEngineUrls.google}${encodeURIComponent(searchStr)}`}
              icon={<FcGoogle />}
            />
            <Tooltip trigger={"hover"} title={"Buzzfile Search"}>
              <Button
                className={styles.table_actions}
                size="small"
                target="_blank"
                rel="noreferrer noopener"
                href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(record?.business_name ? `${record?.business_name}, ` : "")}${encodeURIComponent([record?.first_name, record?.last_name].join(" "))} site:buzzfile.com`}
                icon={<LuPackageSearch />}
              />
            </Tooltip>
            <Tooltip trigger={"hover"} title={"Manta Search"}>
              <Button
                className={styles.table_actions}
                size="small"
                target="_blank"
                rel="noreferrer noopener"
                href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(record?.business_name ? `${record?.business_name}, ` : "")}${encodeURIComponent([record?.first_name, record?.last_name].join(" "))} site:manta.com`}
                icon={<TbWorldSearch />}
              />
            </Tooltip>
            <Button
              className={styles.table_actions}
              size="small"
              onClick={() => {
                setCaseId(record._id);
              }}
            >
              Edit
            </Button>
          </div>
        );
      },
    },
  ];

  const handleUpload = (infoKey: string) => {
    if (infoKey === UPLOAD_TYPE.UPLOAD) {
      const uploadButton = document.getElementById("case_upload_button");
      if (uploadButton) {
        uploadButton.click();
      }
    }
    if (infoKey === UPLOAD_TYPE.DYNAMIC_UPLOAD) {
      //
      setOpenDynamicModalOpen(true);
    }
  };

  return (
    <DashboardLayout
      headerLeft={
        <div className="flex items-center gap-2">
          <Typography.Title level={5} className="!m-0">
            Cases
          </Typography.Title>
          <Button
            icon={<TbExternalLink />}
            type="link"
            onClick={() => {
              detAccess.mutate(undefined, {
                onSuccess: (data) => {
                  window.open(data.result.url, "_blank");
                },
              });
            }}
          >
            V2 Portal
          </Button>
        </div>
      }
      headerRight={
        <div className="flex gap-2 items-center mb-3">
          <div className="flex gap-3 items-center">
            {batchStatus.data?.stats
              ? Object.entries(batchStatus.data?.stats).map(([status, num]) => {
                  return (
                    <Tooltip
                      className="flex flex-col items-center"
                      title={`${status?.toUpperCase()} - ${formatNumberShort(num).actual}`}
                      key={status}
                    >
                      <Tag
                        className="size-5 inline-flex items-center justify-center !p-0 !m-0 !mr-1"
                        icon={(CASE_STATUS_ICONS as any)[status]}
                        color={(CASE_STATUS_COLOURS as any)[status]}
                      ></Tag>
                      <Typography.Text className="!text-xs font-bold">{formatNumberShort(num).short}</Typography.Text>
                    </Tooltip>
                  );
                })
              : null}
          </div>

          <Tooltip title="Edit Mode">
            <Switch size="small" checked={isEditModeOn} onChange={(checked) => setIsEditModeOn(checked)} />
          </Tooltip>
          <Tooltip title="Refresh Data">
            <Button
              icon={<TbRefresh />}
              onClick={() => {
                batchStatus.refetch();
                batchCases.refetch();
              }}
            />
          </Tooltip>
          <Dropdown
            trigger={["click"]}
            destroyPopupOnHide
            dropdownRender={(menus) => {
              return (
                <AssignCases
                  paginatedAllUsers={paginatedAllUsers}
                  onSelect={(optionId) => {
                    assignCases.mutate(
                      { cases: selectedCaseIds, assigned_to: optionId },
                      {
                        onSuccess: () => {
                          message.success("Cases assigned");
                          return batchCases.refetch();
                        },
                      },
                    );
                  }}
                />
              );
            }}
          >
            <Button variant="solid" color="default" icon={<TbUsers />} loading={assignCases.isPending}>
              Assign{" "}
            </Button>
          </Dropdown>
          <Button
            variant="solid"
            color="default"
            icon={<TbDownload />}
            loading={downloadCsv.isPending}
            onClick={() => {
              const body: any = {};
              if (selectedCaseIds.length > 0) {
                body["case_ids"] = selectedCaseIds;
              } else if (filters?.batchId && filters?.batchId?.length > 0) {
                body["batch"] = filters.batchId;
                if (filters.statuses) {
                  body["statuses"] = filters.statuses;
                }
              } else {
                return message.error("Either select a file or individual cases to download");
              }
              message.loading({
                content: "Preparing file to download...",
                key: "download_csv",
                duration: 6,
              });
              downloadCsv.mutate(body, {
                onSuccess: () => {
                  message.success({
                    content: `File ready to download`,
                    key: "download_csv",
                  });
                },
              });
            }}
          >
            Download{" "}
            {selectedCaseIds?.length === 0 ? (
              <span className="w-5">All</span>
            ) : (
              <span className="min-w-5 leading-4 bg-white/15 px-1 py-1 rounded-sm tabular-nums font-semibold">
                {selectedCaseIds?.length}
              </span>
            )}
          </Button>
          <Dropdown
            trigger={["click"]}
            dropdownRender={() => {
              return (
                <div className="bg-white p-3 w-72 shadow-lg border border-solid border-gray-200">
                  <Select
                    placeholder="Select type"
                    style={{ width: "100%", marginBottom: 12 }}
                    value={status.join(",")}
                    options={[
                      { label: "Emails must", value: `${CASE_STATUS.EMAIL_FOUND},${CASE_STATUS.BOTH_FOUND}` },
                      { label: "Phone must", value: `${CASE_STATUS.PHONE_FOUND},${CASE_STATUS.BOTH_FOUND}` },
                      { label: "Email and Phone", value: CASE_STATUS.BOTH_FOUND },
                      {
                        label: "Email or Phone",
                        value: `${CASE_STATUS.EMAIL_FOUND},${CASE_STATUS.PHONE_FOUND},${CASE_STATUS.BOTH_FOUND}`,
                      },
                    ]}
                    onChange={(value) => {
                      setStatus(value.split(",") as CASE_STATUS[]);
                    }}
                  />
                  <DatePicker.RangePicker
                    autoFocus
                    defaultOpen={true}
                    presets={[
                      { label: "Last 7 Days", value: [dayjs().add(-7, "d"), dayjs()] },
                      { label: "Last 14 Days", value: [dayjs().add(-14, "d"), dayjs()] },
                      { label: "Last 30 Days", value: [dayjs().add(-30, "d"), dayjs()] },
                      { label: "Last 90 Days", value: [dayjs().add(-90, "d"), dayjs()] },
                    ]}
                    onChange={(values) => {
                      message.loading({
                        content: "Preparing file to download...",
                        key: "export_email_found_csv",
                        duration: 6,
                      });
                      exportEmailFoundCsv.mutate(
                        {
                          startDate: values?.[0]?.format("YYYY-MM-DD"),
                          endDate: values?.[1]?.format("YYYY-MM-DD"),
                          status,
                        },
                        {
                          onSuccess: () => {
                            message.success({
                              content: `File ready to download`,
                              key: "export_email_found_csv",
                            });
                          },
                        },
                      );
                    }}
                  />
                </div>
              );
            }}
          >
            <Button variant="solid" color="default" icon={<TbTableExport />} loading={exportEmailFoundCsv.isPending}>
              Export
            </Button>
          </Dropdown>

          <Upload
            className="!flex-shrink-0"
            accept=".xls,.xlsx,.csv"
            name="file"
            showUploadList={false}
            customRequest={({ file }) => {
              notification.info({
                key: "file_upload",
                message: "File uploading...",
                icon: <Progress type="circle" percent={progress} size={24} />,
              });
              const formData = new FormData();
              formData.append("file", file);

              const config: AxiosRequestConfig = {
                onUploadProgress: (event) => {
                  if (event?.total) {
                    setProgress((event?.progress ?? 0) * 100);
                  }
                },
              };
              api
                .post("/lead/case/upload", formData, config)
                .then((res: AxiosResponse<T_Api_Success_Res<{ parsed_cases: number; inserted_cases: number }>>) => {
                  // console.log(res.data);
                  // onChange?.(res?.data?.result?.url[0]);

                  notification.success({
                    key: "file_upload",
                    message: "File upload successful",
                  });
                  setTimeout(() => {
                    setProgress(0);
                  }, 500);
                })
                .catch((error) => {
                  notification.error({
                    key: "file_upload",
                    message: getErrorMessage(error),
                  });
                });
            }}
          >
            <Button
              style={{ display: "none" }}
              id="case_upload_button"
              variant="solid"
              color="primary"
              icon={<TbCloudUpload />}
            >
              Upload
            </Button>
          </Upload>
          <Dropdown
            menu={{
              items: [
                { label: UPLOAD_TYPE.UPLOAD, key: UPLOAD_TYPE.UPLOAD },
                { label: UPLOAD_TYPE.DYNAMIC_UPLOAD, key: UPLOAD_TYPE.DYNAMIC_UPLOAD },
              ],
              onClick: (info) => {
                handleUpload(info.key);
              },
              // disabled: selectedCaseIds.length === 0,
            }}
          >
            <Button
              variant="solid"
              // color="default"
              type="primary"
              icon={<TbCloudUpload />}
              loading={runCases.isPending}
              // disabled={selectedCaseIds.length === 0}
            >
              Upload
            </Button>
          </Dropdown>
          <Dropdown
            menu={{
              items: [
                { label: "Extract Field", key: "split" },
                { label: "Enrich LinkedIn", key: "pc" },
                { label: "Enrich Contact Info", key: "bc" },
              ],
              onClick: (info) => {
                handleRun(info.key as any, selectedCaseIds);
              },
              // disabled: selectedCaseIds.length === 0,
            }}
          >
            <Button
              variant="solid"
              color="default"
              className="!bg-green-600 !border-green-600 hover:!bg-green-500 hover:!border-green-500"
              icon={<TbPlayerPlayFilled />}
              loading={runCases.isPending}
              // disabled={selectedCaseIds.length === 0}
            >
              Run
            </Button>
          </Dropdown>
        </div>
      }
    >
      <PageTitle title="Cases" pageTitle={[{ title: "Cases" }]} />
      <div className="flex justify-between gap-3 p-3">
        <div className="inline-flex gap-3 items-center">
          <Segmented
            value={filters.batchType}
            onChange={(value) => setFilters((prev) => ({ ...prev, batchType: value, page: 1 }))}
            options={[
              {
                value: BATCH_TYPE.CASE_PORTAL,
                label: "Case Portal",
              },
              {
                value: BATCH_TYPE.DYNAMIC,
                label: "Dynamic",
              },
            ]}
          />
          <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            File
          </Typography.Text>
          <Select
            style={{ width: 200 }}
            popupMatchSelectWidth={false}
            showSearch
            options={[
              {
                value: "",
                label: "All",
              },
              ...(allBatches.data?.result.map((el) => ({
                value: el._id,
                label: `${el.name} [${el.batch_key.toUpperCase()}]`,
              })) ?? []),
            ]}
            value={filters.batchId}
            onChange={(value) => setFilters((prev) => ({ ...prev, batchId: value, page: 1 }))}
            loading={allBatches.isLoading}
            placeholder="Select Batch"
            optionFilterProp="label"
          />

          <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            Quick Filter
          </Typography.Text>
          <Select
            style={{ width: 160 }}
            placeholder="Quick Filter"
            options={[
              {
                label: "All",
                value: allStatuses.join(","),
              },
              {
                label: "Processed",
                value: processed.join(","),
              },
              {
                label: "Not Processed",
                value: notProcessed.join(","),
              },
            ]}
            value={filters.statuses ? filters.statuses?.join(",") : undefined}
            // mode="multiple"
            maxTagCount={"responsive"}
            onChange={(val: string) => {
              setFilters((prev) => {
                return { ...prev, page: 1, statuses: val?.length > 0 ? val?.split(",") : undefined };
              });
            }}
          />
          <Button
            variant="link"
            color="primary"
            icon={<TbAdjustments />}
            onClick={() => setIsAdvFiltersDrawerOpen(true)}
          >
            Advanced Filter
          </Button>
          {isAdvFiltersDrawerOpen ? (
            <AdvancedFiltersDrawer
              isAdvFiltersDrawerOpen={isAdvFiltersDrawerOpen}
              setIsAdvFiltersDrawerOpen={setIsAdvFiltersDrawerOpen}
              filters={filters}
              setFilters={setFilters}
              paginatedAllUsers={paginatedAllUsers}
              batchCases={batchCases}
              columns={columns}
              showColumns={showColumns}
              setShowColumns={setShowColumns}
            />
          ) : null}
        </div>
        <div className="inline-flex gap-3 items-center">
          <Typography.Text className="italic whitespace-nowrap">
            {erroredQ.length > 0 ? `${erroredQ.length} errored ` : ""}
            {notErroredQ.length > 0 ? `${notErroredQ.length} processing` : ""}
          </Typography.Text>
          <Input
            prefix={<TbSearch />}
            placeholder="Search..."
            onPressEnter={(e) => {
              setFilters((prev) => ({ ...prev, search: e.currentTarget.value }));
            }}
            allowClear
          />
        </div>
      </div>
      {isEditModeOn ? (
        <>
          <table className={styles.edit_table}>
            <thead className="bg-gray-50">
              <tr>
                {editColumns.map((col) => {
                  return (
                    <td key={col?.key} className="h-9" style={{ width: col?.width }}>
                      <Typography.Text className="font-semibold">{col?.title?.toString()}</Typography.Text>
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody style={{ height: "calc(100vh - 204px)", overflowY: "auto" }}>
              {batchCases.isLoading ? loader : null}
              {batchCases.data?.result.data?.map((record, rowIndex) => {
                return (
                  <tr key={record._id}>
                    {editColumns?.map((col, colIndex) => {
                      const key = col?.key as keyof CaseSchemaDto;

                      let comp: any = null;
                      let style: CSSProperties = {};
                      if (["email", "phone"].includes(key)) {
                        const id = `${record._id}_${key}`;
                        const initialValue = record[key];
                        const hasValue = typeof initialValue === "string" && initialValue?.length !== 0;

                        style = { padding: 0 };

                        if (key === "email") {
                          comp = (
                            <Input
                              tabIndex={rowIndex * 10 + colIndex}
                              id={id}
                              style={{ borderRadius: 0, height: 54 }}
                              // variant="filled"
                              defaultValue={initialValue}
                              onPressEnter={(e) => {
                                addToQueue(
                                  {
                                    _id: record._id,
                                    key: id,
                                    [key]: e.currentTarget.value,
                                  },
                                  onError,
                                );
                              }}
                              onBlur={(e) => {
                                addToQueue(
                                  {
                                    _id: record._id,
                                    key: id,
                                    [key]: e.currentTarget.value,
                                  },
                                  onError,
                                );
                              }}
                              // disabled={queue.find((el) => el?.key === id)?._meta?.isProcessing}
                              disabled={hasValue}
                              status={queue.find((el) => el?.key === id)?._meta?.isError ? "error" : undefined}
                            />
                          );
                        } else if (key === "phone") {
                          comp = (
                            <InputNumber
                              type="tel"
                              controls={false}
                              formatter={phoneFormatter}
                              parser={numberParser}
                              //
                              tabIndex={rowIndex * 10 + colIndex}
                              id={id}
                              style={{ width: "100%", borderRadius: 0, height: 54 }}
                              // variant="filled"
                              defaultValue={initialValue}
                              onPressEnter={(e) => {
                                addToQueue(
                                  {
                                    _id: record._id,
                                    key: id,
                                    [key]: e.currentTarget.value,
                                  },
                                  onError,
                                );
                              }}
                              onBlur={(e) => {
                                addToQueue(
                                  {
                                    _id: record._id,
                                    key: id,
                                    [key]: e.currentTarget.value,
                                  },
                                  onError,
                                );
                              }}
                              // disabled={queue.find((el) => el?.key === id)?._meta?.isProcessing}
                              disabled={hasValue}
                              status={queue.find((el) => el?.key === id)?._meta?.isError ? "error" : undefined}
                            />
                          );
                        }
                      } else {
                        if (col?.render) {
                          comp = col?.render(record[key], record, rowIndex);
                        } else {
                          comp = <Typography.Text>{record[key]}</Typography.Text>;
                        }
                      }
                      return (
                        <td key={`${record._id}_${key}`} style={{ width: col?.width, ...style }}>
                          {comp}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination
            className="my-4 justify-end"
            size="small"
            {...{
              total: batchCases?.data?.result?.totalObjects,
              pageSize: filters.limit,
              current: filters.page,
              showSizeChanger: true,
              showPrevNextJumpers: true,
              showQuickJumper: true,
              onChange: (page, pageSize) => {
                setFilters((prev) => ({ ...prev, page, limit: pageSize }));
              },
              showTitle: true,
              showTotal: (total, range) => {
                return `Showing ${range[0]}-${range[1]} of ${total} cases`;
              },
            }}
          />
        </>
      ) : (
        <Table<CaseSchemaDto>
          scroll={{ x: "max-content", y: "calc(100vh - 202px)" }}
          className={styles.case_table}
          loading={{
            spinning: batchCases.isFetching || runCases.isPending,
            indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
          }}
          virtual={false}
          size="small"
          rowSelection={{
            selectedRowKeys: selectedCaseIds,
            getCheckboxProps: (record) => {
              return { name: record._id, disabled: record.hold };
            },
            onChange: (selectedIds) => {
              const allKeys = selectedIds.map((el) => el.toString());
              // if (allKeys.length > 20) {
              //   return message.error("You can only select upto 20 cases at a time");
              // }
              setSelectedCaseIds(allKeys);
            },
          }}
          rowClassName={(row) => (row?.hold ? "progress_loader" : "")}
          columns={columns
            .filter((el) => showColumns[el.key as keyof typeof showColumns])
            .map((el) => {
              if (el.key === "defendant") {
                return {
                  ...el,
                  render: (_, record) => {
                    return (
                      <Typography.Paragraph
                        className="!text-brand-500 !font-medium !cursor-pointer !m-0"
                        onClick={() => {
                          setCaseId(record._id);
                          // updateForm.setFieldsValue(record);
                        }}
                        ellipsis={{ rows: 2, tooltip: record.defendant }}
                      >
                        {record.defendant}
                      </Typography.Paragraph>
                    );
                  },
                };
              } else if (el.key === "actions") {
                return {
                  ...el,
                  render: (_, record) => {
                    const searchStr = `${record?.business_name ? `${record?.business_name}, ` : ""}${[record?.first_name, record?.last_name].join(" ")}`;
                    return (
                      <div className="flex items-center gap-3">
                        <Button
                          className={styles.table_actions}
                          size="small"
                          target="_blank"
                          rel="noreferrer noopener"
                          href={`${searchEngineUrls.google}${encodeURIComponent(searchStr)}`}
                          icon={<FcGoogle />}
                        />
                        <Tooltip trigger={"hover"} title={"Buzzfile Search"}>
                          <Button
                            className={styles.table_actions}
                            size="small"
                            target="_blank"
                            rel="noreferrer noopener"
                            href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(record?.business_name ? `${record?.business_name}, ` : "")}${encodeURIComponent([record?.first_name, record?.last_name].join(" "))} site:buzzfile.com`}
                            icon={<LuPackageSearch />}
                          />
                        </Tooltip>
                        <Tooltip trigger={"hover"} title={"Manta Search"}>
                          <Button
                            className={styles.table_actions}
                            size="small"
                            target="_blank"
                            rel="noreferrer noopener"
                            href={`${searchEngineUrls.duckduckgo}${encodeURIComponent(record?.business_name ? `${record?.business_name}, ` : "")}${encodeURIComponent([record?.first_name, record?.last_name].join(" "))} site:manta.com`}
                            icon={<TbWorldSearch />}
                          />
                        </Tooltip>
                        <Button
                          className={styles.table_actions}
                          size="small"
                          onClick={() => {
                            setCaseId(record._id);
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    );
                  },
                };
              } else return el;
            })}
          dataSource={batchCases.data?.result.data}
          rowKey={(record) => record._id} // Add this line
          pagination={{
            total: batchCases?.data?.result?.totalObjects,
            pageSize: filters.limit,
            current: filters.page,
            showSizeChanger: true,
            showPrevNextJumpers: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
              setFilters((prev) => ({ ...prev, page, limit: pageSize }));
            },
            showTitle: true,
            showTotal: (total, range) => {
              return `Showing ${range[0]}-${range[1]} of ${total} cases`;
            },
          }}
        />
      )}
      {caseId ? <DetailsDrawer caseId={caseId} setCaseId={setCaseId} filters={filters} /> : null}
      <DynamicUpload
        dynamicModalOpen={openDynamicModalOpen}
        setDynamicModalOpen={setOpenDynamicModalOpen}
        fetchLeadDetails={batchCases}
      />
    </DashboardLayout>
  );
}
